/**
 * StretchChallenge Component
 * 
 * Web-based verification for stretch bonus challenges.
 * - 2x grade level = 3x Robux reward
 * - Must be completed on web (anti-cheat)
 * - Parent can watch/verify
 * - Show your work interface
 */

import React, { useState, useEffect } from 'react';

interface StretchChallengeProps {
  childName: string;
  assessedGradeLevel: number; // e.g., 2 for 2nd grade
  onComplete?: (result: { passed: boolean; answer: string; timeSeconds: number }) => void;
  onClose?: () => void;
}

// Stretch challenges by target grade level
const STRETCH_CHALLENGES: Record<number, {
  gradeLevel: number;
  topic: string;
  question: string;
  hint: string;
  correctAnswer: string | number;
  acceptableAnswers: (string | number)[];
  explanation: string;
  baseReward: number;
}[]> = {
  // For 2nd graders → stretch to 6th grade (algebra)
  6: [
    {
      gradeLevel: 6,
      topic: 'Solving Equations',
      question: 'Solve for x: 3x + 7 = 22',
      hint: 'First subtract 7 from both sides, then divide by 3',
      correctAnswer: 5,
      acceptableAnswers: [5, '5', 'x=5', 'x = 5'],
      explanation: '3x + 7 = 22 → 3x = 15 → x = 5',
      baseReward: 50,
    },
    {
      gradeLevel: 6,
      topic: 'Order of Operations',
      question: 'Calculate: 2 + 3 × 4² - 10 ÷ 2',
      hint: 'Remember PEMDAS: Parentheses, Exponents, Multiply/Divide, Add/Subtract',
      correctAnswer: 45,
      acceptableAnswers: [45, '45'],
      explanation: '2 + 3 × 16 - 5 = 2 + 48 - 5 = 45',
      baseReward: 50,
    },
  ],
  // For 2nd-4th graders → stretch to 8th grade (pre-algebra/algebra)
  8: [
    {
      gradeLevel: 8,
      topic: 'Linear Functions',
      question: 'A line passes through (0, 3) and (2, 7). What is the slope?',
      hint: 'Slope = rise/run = (y₂ - y₁)/(x₂ - x₁)',
      correctAnswer: 2,
      acceptableAnswers: [2, '2', '2/1', '4/2'],
      explanation: 'Slope = (7-3)/(2-0) = 4/2 = 2',
      baseReward: 75,
    },
    {
      gradeLevel: 8,
      topic: 'Systems Preview',
      question: 'If y = 2x and y = x + 3, what is x?',
      hint: 'Set the two expressions for y equal to each other',
      correctAnswer: 3,
      acceptableAnswers: [3, '3', 'x=3', 'x = 3'],
      explanation: '2x = x + 3 → x = 3',
      baseReward: 75,
    },
  ],
  // For 4th-6th graders → stretch to 10th grade (algebra 2)
  10: [
    {
      gradeLevel: 10,
      topic: 'Quadratic Equations',
      question: 'Solve: x² - 5x + 6 = 0',
      hint: 'Factor into (x - ?)(x - ?) = 0. What two numbers multiply to 6 and add to -5?',
      correctAnswer: '2 and 3',
      acceptableAnswers: ['2 and 3', '3 and 2', 'x=2, x=3', 'x=3, x=2', '2, 3', '3, 2', '{2,3}', '{3,2}'],
      explanation: '(x-2)(x-3) = 0, so x = 2 or x = 3',
      baseReward: 100,
    },
    {
      gradeLevel: 10,
      topic: 'Parabola Analysis',
      question: 'The ball\'s height is h(t) = -16t² + 48t + 4. At what time t does it reach maximum height?',
      hint: 'Maximum of a parabola ax² + bx + c is at t = -b/(2a)',
      correctAnswer: 1.5,
      acceptableAnswers: [1.5, '1.5', '3/2', 1.5, '1.5 seconds', '1.5s'],
      explanation: 't = -48/(2×-16) = -48/-32 = 1.5 seconds',
      baseReward: 100,
    },
  ],
  // For advanced kids → stretch to 12th grade (pre-calc/calc)
  12: [
    {
      gradeLevel: 12,
      topic: 'Derivatives Intro',
      question: 'If f(x) = x³, what is f\'(x)?',
      hint: 'Power rule: derivative of xⁿ is n·xⁿ⁻¹',
      correctAnswer: '3x²',
      acceptableAnswers: ['3x²', '3x^2', '3*x^2', '3x2'],
      explanation: 'Using the power rule: d/dx(x³) = 3x²',
      baseReward: 150,
    },
    {
      gradeLevel: 12,
      topic: 'Optimization',
      question: 'Find the minimum value of f(x) = x² - 4x + 7',
      hint: 'Complete the square or use vertex formula x = -b/(2a), then find f(x)',
      correctAnswer: 3,
      acceptableAnswers: [3, '3', 'f(x)=3', 'y=3'],
      explanation: 'Vertex at x = 4/(2×1) = 2. f(2) = 4 - 8 + 7 = 3',
      baseReward: 150,
    },
  ],
};

function getStretchLevel(assessedGrade: number): number {
  // 2x grade level, capped at 12
  return Math.min(12, assessedGrade * 2);
}

export function StretchChallenge({
  childName,
  assessedGradeLevel,
  onComplete,
  onClose,
}: StretchChallengeProps) {
  const stretchLevel = getStretchLevel(assessedGradeLevel);
  const challenges = STRETCH_CHALLENGES[stretchLevel] || STRETCH_CHALLENGES[6];
  
  const [currentChallenge] = useState(() => 
    challenges[Math.floor(Math.random() * challenges.length)]
  );
  
  const [answer, setAnswer] = useState('');
  const [showHint, setShowHint] = useState(false);
  const [showWork, setShowWork] = useState('');
  const [startTime] = useState(Date.now());
  const [result, setResult] = useState<'pending' | 'correct' | 'incorrect'>('pending');
  const [timeElapsed, setTimeElapsed] = useState(0);

  // Timer
  useEffect(() => {
    if (result !== 'pending') return;
    const timer = setInterval(() => {
      setTimeElapsed(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [startTime, result]);

  const checkAnswer = () => {
    const normalizedAnswer = answer.trim().toLowerCase();
    const isCorrect = currentChallenge.acceptableAnswers.some(
      acceptable => String(acceptable).toLowerCase() === normalizedAnswer
    );
    
    setResult(isCorrect ? 'correct' : 'incorrect');
    
    const timeSeconds = Math.floor((Date.now() - startTime) / 1000);
    onComplete?.({
      passed: isCorrect,
      answer: answer,
      timeSeconds,
    });
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const bonusMultiplier = 3;
  const totalReward = currentChallenge.baseReward * bonusMultiplier;
  const gradeJump = stretchLevel - assessedGradeLevel;

  return (
    <div className="rounded-2xl bg-blox-card border border-amber-500/30 overflow-hidden max-w-2xl w-full">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 p-6 border-b border-amber-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-4xl">🏆</span>
            <div>
              <h2 className="font-display text-xl text-white">Stretch Challenge</h2>
              <p className="text-sm text-amber-400">
                +{gradeJump} grade levels • {bonusMultiplier}x Robux bonus!
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-amber-400">+{totalReward}</div>
            <div className="text-xs text-slate-400">Robux reward</div>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Challenge Info */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-amber-500/20 text-amber-400 rounded text-xs font-semibold">
              Grade {stretchLevel} Level
            </span>
            <span className="text-slate-400">{currentChallenge.topic}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <span>⏱️</span>
            <span className="font-mono">{formatTime(timeElapsed)}</span>
          </div>
        </div>

        {/* Question */}
        <div className="bg-slate-800/80 rounded-xl p-6">
          <p className="text-lg text-white leading-relaxed">
            {currentChallenge.question}
          </p>
        </div>

        {/* Hint Toggle */}
        {!showHint && result === 'pending' && (
          <button
            onClick={() => setShowHint(true)}
            className="text-sm text-slate-400 hover:text-amber-400 transition-colors"
          >
            💡 Need a hint?
          </button>
        )}
        
        {showHint && (
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <p className="text-sm text-amber-300">
              💡 <span className="font-semibold">Hint:</span> {currentChallenge.hint}
            </p>
          </div>
        )}

        {result === 'pending' ? (
          <>
            {/* Show Your Work */}
            <div>
              <label className="block text-sm font-semibold text-slate-400 mb-2">
                Show your work (optional but encouraged!)
              </label>
              <textarea
                value={showWork}
                onChange={(e) => setShowWork(e.target.value)}
                placeholder="Write out your steps here..."
                className="w-full px-4 py-3 rounded-xl bg-blox-dark border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 min-h-[100px] text-sm"
              />
            </div>

            {/* Answer Input */}
            <div>
              <label className="block text-sm font-semibold text-slate-400 mb-2">
                Your answer
              </label>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                placeholder="Enter your answer..."
                className="w-full px-4 py-3 rounded-xl bg-blox-dark border border-slate-700 text-white placeholder-slate-500 focus:outline-none focus:border-amber-500/50 text-lg"
                autoFocus
              />
            </div>

            {/* Submit */}
            <div className="flex gap-3">
              {onClose && (
                <button
                  onClick={onClose}
                  className="px-6 py-3 rounded-xl border border-slate-600 text-slate-400 font-semibold hover:bg-blox-surface transition-colors"
                >
                  Cancel
                </button>
              )}
              <button
                onClick={checkAnswer}
                disabled={!answer.trim()}
                className="flex-1 px-6 py-3 rounded-xl bg-amber-500 text-blox-dark font-bold hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Submit Answer
              </button>
            </div>
          </>
        ) : (
          /* Result */
          <div className={`rounded-xl p-6 ${
            result === 'correct' 
              ? 'bg-emerald-500/20 border border-emerald-500/30' 
              : 'bg-red-500/20 border border-red-500/30'
          }`}>
            {result === 'correct' ? (
              <div className="text-center">
                <div className="text-5xl mb-3">🎉</div>
                <h3 className="text-2xl font-bold text-emerald-400 mb-2">
                  Amazing, {childName}!
                </h3>
                <p className="text-slate-300 mb-4">
                  You solved a Grade {stretchLevel} problem! That's {gradeJump} levels above your assessed grade!
                </p>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/20 rounded-full">
                  <span className="text-2xl font-bold text-emerald-400">+{totalReward}</span>
                  <span className="text-emerald-300">Robux earned!</span>
                </div>
                <div className="mt-4 text-sm text-slate-400">
                  Completed in {formatTime(timeElapsed)}
                </div>
              </div>
            ) : (
              <div className="text-center">
                <div className="text-5xl mb-3">🤔</div>
                <h3 className="text-xl font-bold text-red-400 mb-2">
                  Not quite, but great try!
                </h3>
                <p className="text-slate-300 mb-4">
                  {currentChallenge.explanation}
                </p>
                <p className="text-sm text-slate-400">
                  Stretch challenges are meant to push you. Keep practicing!
                </p>
              </div>
            )}
            
            {onClose && (
              <button
                onClick={onClose}
                className="w-full mt-4 px-6 py-3 rounded-xl bg-slate-700 text-white font-semibold hover:bg-slate-600 transition-colors"
              >
                Done
              </button>
            )}
          </div>
        )}

        {/* Verification Notice */}
        {result === 'pending' && (
          <div className="flex items-center gap-2 text-xs text-slate-500 justify-center">
            <span>🔐</span>
            <span>Web verification ensures authentic achievement • Parent can observe</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default StretchChallenge;
